import React, { useCallback, useMemo, useState } from "react";
import { Download, Filter, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import Filters from "../../Filters";
import DatePicker from "../../../Common/DatePicker";
import { attendanceService, employeeService } from "../../../../services/api";
import {
  exportAttendanceReportExcel,
  exportAttendanceReportPdf,
} from "./exportAttendanceReport";

const TODAY = new Date().toISOString().slice(0, 10);

const DAILY_FORMATS = [
  { id: "detail", label: "Detail (day-wise)" },
  { id: "summary", label: "Summary (counts by employee)" },
  { id: "status_matrix", label: "Status matrix" },
];

const LEAVE_FORMATS = [
  { id: "summary", label: "Summary" },
  { id: "leave_type_wise", label: "Leave type wise" },
  { id: "availed_only", label: "Availed only" },
  { id: "credited_only", label: "Credited only" },
  { id: "adjusted_only", label: "Adjusted only" },
  { id: "lapsed_only", label: "Lapsed only" },
];

function applyClientFilters(baseEmployees, filters) {
  if (!baseEmployees?.length) return [];
  return baseEmployees.filter((emp) => {
    const matchMulti = (field, empVal) => {
      const raw = filters?.[field];
      if (!raw || (Array.isArray(raw) && raw.length === 0)) return true;
      const list = Array.isArray(raw) ? raw : [raw];
      if (list.includes("__all__")) return true;
      return list.some((v) => String(v) === String(empVal));
    };
    const selectedIds = filters?.selectedEmployeeIds || [];
    if (selectedIds.length && !selectedIds.map(String).includes(String(emp.id))) {
      return false;
    }
    return (
      matchMulti("branch", emp.branchId ?? emp.branch?.id) &&
      matchMulti("department", emp.departmentId ?? emp.department?.id) &&
      matchMulti("designation", emp.designationId ?? emp.designation?.id) &&
      matchMulti("grade", emp.gradeId ?? emp.grade?.id) &&
      matchMulti("category", emp.categoryId ?? emp.category?.id) &&
      matchMulti("status", emp.employmentStatus)
    );
  });
}

function reportConfig(reportKey) {
  switch (reportKey) {
    case "attendance-checks":
      return {
        title: "Attendance Checks",
        columns: [
          { key: "slNo", header: "Sl. No" },
          { key: "empCode", header: "Employee Code" },
          { key: "name", header: "Name" },
          { key: "date", header: "Date" },
          { key: "time", header: "Time" },
          { key: "inOut", header: "IN/OUT" },
          { key: "source", header: "Source" },
          { key: "location", header: "Location" },
        ],
        dateRange: true,
      };
    case "daily-attendance":
      return {
        title: "Daily Attendance Report",
        columns: [
          { key: "slNo", header: "Sl. No" },
          { key: "empCode", header: "Employee Code" },
          { key: "name", header: "Name" },
          { key: "date", header: "Date" },
          { key: "firstIn", header: "First IN" },
          { key: "lastOut", header: "Last OUT" },
          { key: "status", header: "Status" },
          { key: "remarks", header: "Remarks" },
        ],
        dateRange: true,
        formatOptions: DAILY_FORMATS,
      };
    case "who-are-in":
      return {
        title: "Who are in",
        columns: [
          { key: "slNo", header: "Sl. No" },
          { key: "empCode", header: "Employee Code" },
          { key: "name", header: "Name" },
          { key: "date", header: "Date" },
          { key: "time", header: "Time" },
          { key: "latestCheckIn", header: "Latest Check IN" },
          { key: "source", header: "Source" },
          { key: "location", header: "Location" },
        ],
        singleDate: true,
      };
    case "leave-balance-report":
      return {
        title: "Leave Balance Report",
        columns: [
          { key: "slNo", header: "Sl. No" },
          { key: "empCode", header: "Employee Code" },
          { key: "name", header: "Name" },
          { key: "leaveType", header: "Leave Type" },
          { key: "opening", header: "Opening" },
          { key: "credited", header: "Credited" },
          { key: "availed", header: "Availed" },
          { key: "adjusted", header: "Adjusted" },
          { key: "lapsed", header: "Lapsed" },
          { key: "closing", header: "Closing" },
        ],
        monthRange: true,
        formatOptions: LEAVE_FORMATS,
      };
    default:
      return { title: "Report", columns: [] };
  }
}

export default function AttendanceFilteredReportPanel({ reportKey, darkMode = false }) {
  const cfg = useMemo(() => reportConfig(reportKey), [reportKey]);
  const [showFilters, setShowFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [baseEmployees, setBaseEmployees] = useState([]);
  const [filteredEmployeeIds, setFilteredEmployeeIds] = useState([]);
  const [dateRange, setDateRange] = useState({ from: TODAY, to: TODAY });
  const [asOfDate, setAsOfDate] = useState(TODAY);
  const [year, setYear] = useState(new Date().getFullYear());
  const [fromMonth, setFromMonth] = useState(1);
  const [toMonth, setToMonth] = useState(new Date().getMonth() + 1);
  const [format, setFormat] = useState(cfg.formatOptions?.[0]?.id || "detail");
  const [rows, setRows] = useState([]);
  const [matrixDates, setMatrixDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const ensureEmployees = useCallback(async () => {
    if (baseEmployees.length) return baseEmployees;
    const resp = await employeeService.getEmployees({
      excludeEmploymentTypes: "External,External",
    });
    let list = [];
    if (Array.isArray(resp)) list = resp;
    else if (Array.isArray(resp?.data)) list = resp.data;
    else if (Array.isArray(resp?.employees)) list = resp.employees;
    setBaseEmployees(list);
    return list;
  }, [baseEmployees.length]);

  const resolveEmployeeIds = useCallback(
    async (filters) => {
      const base = await ensureEmployees();
      const filtered = applyClientFilters(base, filters || appliedFilters || {});
      const ids = filtered.map((e) => Number(e.id)).filter((id) => id > 0);
      setFilteredEmployeeIds(ids);
      return ids;
    },
    [appliedFilters, ensureEmployees]
  );

  const loadReport = async () => {
    setLoading(true);
    try {
      const employeeIds = await resolveEmployeeIds(appliedFilters);
      let res;
      if (reportKey === "attendance-checks") {
        if (!dateRange.from || !dateRange.to) {
          toast.error("Select From and To dates");
          return;
        }
        res = await attendanceService.getAttendanceChecksReport({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          employeeIds: employeeIds.join(","),
        });
        setRows(res?.data?.rows || []);
        setMatrixDates([]);
      } else if (reportKey === "daily-attendance") {
        if (!dateRange.from || !dateRange.to) {
          toast.error("Select From and To dates");
          return;
        }
        res = await attendanceService.getDailyAttendanceReportV2({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          format,
          employeeIds: employeeIds.join(","),
        });
        const data = res?.data || {};
        if (format === "status_matrix") {
          setMatrixDates(data.dates || []);
          setRows(data.rows || []);
        } else {
          setMatrixDates([]);
          setRows(data.rows || []);
        }
      } else if (reportKey === "who-are-in") {
        res = await attendanceService.getWhoAreInReport({
          asOfDate: asOfDate || TODAY,
          employeeIds: employeeIds.join(","),
        });
        setRows(res?.data?.rows || []);
        setMatrixDates([]);
      } else if (reportKey === "leave-balance-report") {
        res = await attendanceService.getLeaveBalanceReport({
          year,
          fromMonth,
          toMonth,
          format,
          employeeIds: employeeIds.join(","),
        });
        setRows(res?.data?.rows || []);
        setMatrixDates([]);
      }
      const data = res?.data || {};
      setLoaded(true);
      toast.success(`Loaded ${data.rows?.length ?? 0} row(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (kind) => {
    if (!loaded || !rows.length) {
      toast.error("Load the report first");
      return;
    }
    const subtitle =
      reportKey === "who-are-in"
        ? `As on ${asOfDate}`
        : reportKey === "leave-balance-report"
          ? `Year ${year} · Months ${fromMonth}–${toMonth}`
          : `${dateRange.from} to ${dateRange.to}`;
    const fileBase = cfg.title.replace(/\s+/g, "-").toLowerCase();
    if (kind === "excel") {
      exportAttendanceReportExcel({
        rows,
        columns: cfg.columns,
        sheetName: cfg.title,
        fileName: `${fileBase}.xlsx`,
        metaRows: [[cfg.title], [subtitle], []],
      });
    } else {
      exportAttendanceReportPdf({
        title: cfg.title,
        subtitle,
        rows,
        columns: cfg.columns,
        fileName: `${fileBase}.pdf`,
      });
    }
    toast.success(`${kind === "excel" ? "Excel" : "PDF"} exported`);
  };

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-gray-200";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {showFilters && (
        <Filters
          darkMode={darkMode}
          onApply={async (_list, filters) => {
            setAppliedFilters(filters || null);
            await resolveEmployeeIds(filters);
            setShowFilters(false);
            toast.success("Filters applied");
          }}
          onClose={() => setShowFilters(false)}
          onClear={() => {
            setAppliedFilters(null);
            setFilteredEmployeeIds([]);
          }}
          initialFilters={{ dateFilterType: "all" }}
        />
      )}

      <div className={`flex flex-wrap items-end gap-3 rounded-lg border p-3 ${border}`}>
        {cfg.formatOptions && (
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
              Format
            </label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className={`rounded-lg border px-2 py-1.5 text-xs ${border}`}
            >
              {cfg.formatOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {cfg.dateRange && (
          <>
            <DatePicker
              label="From"
              value={dateRange.from}
              onChange={(e) =>
                setDateRange((p) => ({ ...p, from: String(e?.target?.value || "") }))
              }
              darkMode={darkMode}
              max={dateRange.to || TODAY}
            />
            <DatePicker
              label="To"
              value={dateRange.to}
              onChange={(e) =>
                setDateRange((p) => ({ ...p, to: String(e?.target?.value || "") }))
              }
              darkMode={darkMode}
              min={dateRange.from}
              max={TODAY}
            />
          </>
        )}
        {cfg.singleDate && (
          <DatePicker
            label="Date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(String(e?.target?.value || ""))}
            darkMode={darkMode}
            max={TODAY}
          />
        )}
        {cfg.monthRange && (
          <>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase">Year</label>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={`w-24 rounded border px-2 py-1.5 text-xs ${border}`} />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase">From month</label>
              <input type="number" min={1} max={12} value={fromMonth} onChange={(e) => setFromMonth(Number(e.target.value))} className={`w-20 rounded border px-2 py-1.5 text-xs ${border}`} />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase">To month</label>
              <input type="number" min={1} max={12} value={toMonth} onChange={(e) => setToMonth(Number(e.target.value))} className={`w-20 rounded border px-2 py-1.5 text-xs ${border}`} />
            </div>
          </>
        )}
        <button type="button" onClick={() => setShowFilters(true)} className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs ${border}`}>
          <Filter size={14} /> Filters
          {filteredEmployeeIds.length > 0 && (
            <span className="rounded-full bg-[hsl(var(--ds-primary))]/15 px-1.5 text-[10px]">
              {filteredEmployeeIds.length}
            </span>
          )}
        </button>
        <button type="button" disabled={loading} onClick={loadReport} className="rounded bg-[hsl(var(--ds-primary))] px-4 py-1.5 text-xs font-medium text-white disabled:opacity-50">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Load"}
        </button>
        <button type="button" disabled={!loaded} onClick={() => handleExport("excel")} className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs disabled:opacity-40 ${border}`}>
          <Download size={14} /> Excel
        </button>
        <button type="button" disabled={!loaded} onClick={() => handleExport("pdf")} className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs disabled:opacity-40 ${border}`}>
          <Download size={14} /> PDF
        </button>
      </div>

      <div className={`min-h-0 flex-1 overflow-auto rounded-lg border ${border}`}>
        {!loaded ? (
          <p className="p-8 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
            Apply filters if needed, set date range, then click Load to view {cfg.title}.
          </p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-[hsl(var(--ds-muted-fg))]">No records for the selected criteria.</p>
        ) : format === "status_matrix" && matrixDates.length ? (
          <table className="w-full min-w-[900px] text-xs">
            <thead className="sticky top-0 bg-[hsl(var(--ds-muted))]">
              <tr>
                <th className="px-2 py-2 text-left">Sl.</th>
                <th className="px-2 py-2 text-left">Code</th>
                <th className="px-2 py-2 text-left">Name</th>
                {matrixDates.map((d) => (
                  <th key={d} className="px-1 py-2 text-center">{d.slice(8)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employeeId} className="border-t border-[hsl(var(--ds-border))]">
                  <td className="px-2 py-1">{r.slNo}</td>
                  <td className="px-2 py-1">{r.empCode}</td>
                  <td className="px-2 py-1">{r.name}</td>
                  {matrixDates.map((d) => (
                    <td key={d} className="px-1 py-1 text-center">{r.days?.[d] || "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full min-w-[720px] text-xs">
            <thead className="sticky top-0 bg-[hsl(var(--ds-muted))]">
              <tr>
                {cfg.columns.map((c) => (
                  <th key={c.key} className="px-2 py-2 text-left">{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.employeeId}-${r.date}-${i}`} className="border-t border-[hsl(var(--ds-border))]">
                  {cfg.columns.map((c) => (
                    <td key={c.key} className="px-2 py-1 align-top">{r[c.key] ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
